import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, TrendingUp, BarChart3, Calculator, BookOpen, Menu, X, Quote, Users, Target, Briefcase, Award, Clock, ChevronRight, Star, Building2, Rocket, DollarSign, FileSpreadsheet, PieChart, LineChart } from 'lucide-react';
import { Button } from '../components/ui/button';

export const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const observerRefs = useRef([]);

  const questions = [
    {
      title: 'Can you do it',
      description: 'We build your finance engine from zero to investor ready.',
      number: '01',
      details: [
        'Chart of accounts & accounting system setup',
        '3-statement financial model with projections',
        'KPI dashboard & metrics tracking',
        'Investor-ready data room preparation'
      ]
    },
    {
      title: 'I already do it. Can you make it easier',
      description: 'We simplify, automate and remove friction from your sheets and processes.',
      number: '02',
      details: [
        'Process audit & streamlining',
        'Automated reporting workflows',
        'Real-time dashboard integration',
        'Month-end close optimization'
      ]
    },
    {
      title: 'It is neither simple nor accurate. Can you add meaning',
      description: 'We bring depth, intelligence and narrative into numbers.',
      number: '03',
      details: [
        'Variance analysis & insights',
        'Scenario modeling & forecasting',
        'Board-ready financial narratives',
        'Strategic decision support'
      ]
    }
  ];

  const changes = [
    'Finance is feared. We demystify it.',
    'Building models feels heavy. We make them breathable.',
    'Finance is seen as a cost. We are profit leverage.',
    'Complexity is admired. Clarity wins.',
    'Forecast lives in sheets, actuals live somewhere else. We connect the two.',
    'One pivot breaks everything. We build adaptable models.',
    'Founders work alone in finance. With us, they do not.'
  ];

  const testimonials = [
    {
      quote: "They transformed our messy spreadsheets into a financial model that actually helped us close our Series A. The clarity they brought was game-changing.",
      author: "Sarah Chen",
      role: "CEO & Co-founder",
      company: "TechFlow SaaS",
      result: "Raised $4.2M Series A"
    },
    {
      quote: "Finally, a CFO service that speaks founder language. No jargon, just actionable insights that helped us extend our runway by 8 months.",
      author: "Marcus Johnson",
      role: "Founder",
      company: "GrowthStack",
      result: "Extended runway 8 months"
    },
    {
      quote: "Our board meetings went from stressful to strategic. The financial narratives they create tell our story in numbers investors understand.",
      author: "Priya Patel",
      role: "CEO",
      company: "FinLeap",
      result: "3x faster board prep"
    }
  ];

  const idealClients = [
    {
      icon: Rocket,
      stage: "Pre-Seed to Series A",
      description: "Startups preparing for or recently completing fundraising",
      revenue: "$0 - $2M ARR"
    },
    {
      icon: Building2,
      stage: "SaaS & Tech Companies",
      description: "Subscription businesses needing metrics-driven finance",
      revenue: "MRR-focused models"
    },
    {
      icon: Target,
      stage: "Growth-Stage Startups",
      description: "Companies scaling operations and team",
      revenue: "$2M - $10M ARR"
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Discovery Call",
      description: "30-minute conversation to understand your specific challenges, goals, and current financial state.",
      duration: "30 min"
    },
    {
      step: "02",
      title: "Tailored Proposal",
      description: "We present a custom scope and investment based on your needs. No cookie-cutter packages.",
      duration: "2-3 days"
    },
    {
      step: "03",
      title: "Onboarding & Execution",
      description: "Seamless handoff with clear milestones. You get clarity from week one.",
      duration: "Ongoing"
    }
  ];

  // Scroll handler for parallax
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % questions.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [questions.length]);

  // Auto-scroll testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observers = [];
    observerRefs.current.forEach((ref, index) => {
      if (ref) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setVisibleItems(prev => new Set([...prev, index]));
            }
          },
          { threshold: 0.2 }
        );
        observer.observe(ref);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Floating Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto">
          <div className="glass-nav rounded-xl sm:rounded-2xl px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <div className="relative">
                <TrendingUp className="h-5 w-5 sm:h-7 sm:w-7 text-[#C9A64A]" />
                <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 text-[#C9A64A] absolute -bottom-1 -right-1" />
              </div>
              <span className="text-lg sm:text-xl font-bold text-white tracking-tight">Virtual<span className="text-[#C9A64A]">CFO</span></span>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              <Link to="/approach" className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium text-sm">Approach</Link>
              <Link to="/presentations" className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium text-sm">Presentations</Link>
              <Link to="/free-resources" className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium text-sm">Downloads</Link>
              <Link to="/calculators" className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium flex items-center gap-2 text-sm">
                <Calculator className="h-4 w-4" />
                Calculators
              </Link>
              <Link to="/articles" className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium flex items-center gap-2 text-sm">
                <BookOpen className="h-4 w-4" />
                Articles
              </Link>
              <Link to="/contact" className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium text-sm">Contact</Link>
            </nav>
            
            <div className="flex items-center gap-3">
              <Link to="/contact">
                <Button 
                  size="sm"
                  className="hidden sm:flex bg-[#C9A64A] text-[#0F1A2A] hover:bg-white hover:scale-105 transition-all duration-300 font-semibold shadow-lg text-xs sm:text-sm"
                >
                  Get Started
                </Button>
              </Link>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-white hover:text-[#C9A64A] transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-2 glass-nav rounded-xl p-4 animate-fade-in-up">
              <nav className="flex flex-col gap-3">
                <Link 
                  to="/approach" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium py-2 px-3 rounded-lg hover:bg-white/5"
                >
                  Approach
                </Link>
                <Link 
                  to="/presentations" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium py-2 px-3 rounded-lg hover:bg-white/5"
                >
                  Presentations
                </Link>
                <Link 
                  to="/free-resources" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium py-2 px-3 rounded-lg hover:bg-white/5"
                >
                  Downloads
                </Link>
                <Link 
                  to="/calculators" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium py-2 px-3 rounded-lg hover:bg-white/5 flex items-center gap-2"
                >
                  <Calculator className="h-4 w-4" />
                  Calculators
                </Link>
                <Link 
                  to="/articles" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium py-2 px-3 rounded-lg hover:bg-white/5 flex items-center gap-2"
                >
                  <BookOpen className="h-4 w-4" />
                  Articles
                </Link>
                <Link 
                  to="/contact" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-200 hover:text-[#C9A64A] transition-colors duration-300 font-medium py-2 px-3 rounded-lg hover:bg-white/5"
                >
                  Contact
                </Link>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    size="sm"
                    className="mt-2 w-full bg-[#C9A64A] text-[#0F1A2A] hover:bg-white transition-all duration-300 font-semibold"
                  >
                    Get Started
                  </Button>
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-gradient relative min-h-screen flex items-center justify-center px-4 sm:px-6 py-24 sm:py-32 overflow-hidden">
        {/* Background Image - Faded */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-[0.08]"
            style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzY0MTUzODYzfDA&ixlib=rb-4.1.0&q=85)' }}
          />
          <div className="absolute top-20 right-10 sm:right-20 w-48 sm:w-96 h-48 sm:h-96 bg-[#C9A64A] opacity-10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 left-10 sm:left-20 w-48 sm:w-96 h-48 sm:h-96 bg-[#5A6270] opacity-10 rounded-full blur-3xl animate-float-delayed" />
        </div>

        {/* Grain Texture Overlay */}
        <div className="grain-overlay" />

        <div 
          className="max-w-6xl mx-auto text-center space-y-8 sm:space-y-16 relative z-10"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          {/* Accent Line */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-4 sm:mb-8">
            <div className="h-px w-8 sm:w-16 bg-gradient-to-r from-transparent to-[#C9A64A]" />
            <span className="text-[#C9A64A] text-xs sm:text-sm font-semibold tracking-widest uppercase">Financial Excellence</span>
            <div className="h-px w-8 sm:w-16 bg-gradient-to-l from-transparent to-[#C9A64A]" />
          </div>

          <div className="space-y-4 sm:space-y-8 animate-fade-in-up">
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold tracking-tight leading-[1.1] text-white px-2">
              Virtual CFO for<br />
              <span className="text-gradient">clarity and control.</span>
            </h1>
            <div className="inline-block">
              <p className="text-lg sm:text-2xl md:text-3xl font-light text-gray-300 leading-relaxed">
                Clutter everywhere.<br />
                <span className="text-[#C9A64A] font-medium">Not here.</span>
              </p>
            </div>
          </div>
          
          <p className="text-base sm:text-xl md:text-2xl text-gray-300 font-light max-w-4xl mx-auto leading-relaxed px-4">
            We guide fast moving founders through financial chaos with models that breathe, scale and make sense.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center pt-6 sm:pt-12 px-4">
            <Link to="/contact">
              <Button 
                size="lg"
                className="premium-button w-full sm:w-auto bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:shadow-2xl hover:scale-105 transition-all duration-500 px-6 sm:px-10 py-5 sm:py-7 text-base sm:text-lg font-bold rounded-xl group"
              >
                Start with your financial model
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </Link>
            <Link to="/calculators">
              <Button 
                size="lg"
                className="glass-button w-full sm:w-auto border-2 border-white/30 text-white hover:border-[#C9A64A] hover:bg-white/10 backdrop-blur-sm transition-all duration-500 px-6 sm:px-10 py-5 sm:py-7 text-base sm:text-lg font-semibold rounded-xl group"
              >
                <Calculator className="mr-2 h-5 w-5" />
                Try Free Calculators
              </Button>
            </Link>
          </div>

          {/* Scroll Indicator - Hidden on mobile */}
          <div className="hidden sm:block absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="flex flex-col items-center gap-2">
              <span className="text-gray-400 text-sm font-medium">Scroll to explore</span>
              <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex items-start justify-center p-2">
                <div className="w-1 h-2 bg-gray-400 rounded-full animate-scroll" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Statement */}
      <section className="py-20 sm:py-40 px-4 sm:px-6 bg-white relative">
        <div className="max-w-5xl mx-auto">
          <div className="glass-card p-8 sm:p-12 md:p-16 text-center rounded-2xl sm:rounded-3xl">
            <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] mx-auto mb-8 sm:mb-12 rounded-full" />
            <p className="text-xl sm:text-3xl md:text-4xl font-light text-[#1A1A1C] leading-relaxed">
              A Virtual CFO should not confuse you with more sheets.
            </p>
            <p className="text-xl sm:text-3xl md:text-4xl font-semibold text-[#1A1A1C] leading-relaxed mt-4 sm:mt-6">
              We keep it simple, structured, and outcome-driven.
            </p>
          </div>
        </div>
      </section>

      {/* Three Questions Section */}
      <section className="py-20 sm:py-40 px-4 sm:px-6 bg-gradient-to-b from-white to-[#F8F8F8] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C9A64A] to-transparent opacity-30" />
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-24 space-y-4 sm:space-y-8 animate-fade-in-up">
            <div className="inline-block">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="h-px w-8 sm:w-12 bg-[#C9A64A]" />
                <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">The Foundation</span>
                <div className="h-px w-8 sm:w-12 bg-[#C9A64A]" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold text-[#1A1A1C] px-2">
              Only Three Questions Matter
            </h2>
            <p className="text-base sm:text-xl md:text-2xl text-[#5A6270] font-light max-w-4xl mx-auto leading-relaxed px-4">
              Before models, forecasts, dashboards or data dumps, only three questions matter.<br className="hidden sm:block" />
              <span className="text-[#1A1A1C] font-medium">Ask us any one and we begin.</span>
            </p>
          </div>

          {/* Premium Cards Slider */}
          <div className="relative">
            <div className="overflow-hidden rounded-2xl sm:rounded-3xl">
              <div 
                className="flex transition-transform duration-1000 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {questions.map((question, index) => (
                  <div key={index} className="min-w-full px-2 sm:px-4">
                    <div className="premium-question-card relative bg-gradient-to-br from-[#1A1A1C] to-[#0F1A2A] text-white p-8 sm:p-16 md:p-20 rounded-2xl sm:rounded-3xl hover:shadow-premium transition-all duration-700 transform hover:-translate-y-4 cursor-pointer group overflow-hidden">
                      {/* Number Badge */}
                      <div className="absolute top-4 right-4 sm:top-8 sm:right-8 w-12 h-12 sm:w-20 sm:h-20 rounded-full bg-[#C9A64A]/20 backdrop-blur-sm flex items-center justify-center border border-[#C9A64A]/40">
                        <span className="text-[#C9A64A] text-lg sm:text-2xl font-bold">{question.number}</span>
                      </div>

                      {/* Decorative Corner */}
                      <div className="absolute bottom-0 right-0 w-32 sm:w-64 h-32 sm:h-64 bg-gradient-to-tl from-[#C9A64A]/10 to-transparent rounded-tl-full transform translate-x-16 translate-y-16 sm:translate-x-32 sm:translate-y-32 group-hover:translate-x-12 group-hover:translate-y-12 sm:group-hover:translate-x-24 sm:group-hover:translate-y-24 transition-transform duration-700" />
                      
                      <div className="relative z-10 pr-12 sm:pr-0">
                        <h3 className="text-xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-8 leading-tight">
                          {question.title}
                        </h3>
                        <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-[#C9A64A] to-transparent mb-4 sm:mb-8 rounded-full" />
                        <p className="text-base sm:text-xl md:text-2xl font-light opacity-90 leading-relaxed text-gray-300 mb-6 sm:mb-8">
                          {question.description}
                        </p>
                        
                        {/* What's Included */}
                        <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-white/10">
                          <h4 className="text-sm sm:text-base font-semibold text-[#C9A64A] mb-4 uppercase tracking-wider">What's Included</h4>
                          <ul className="space-y-3">
                            {question.details.map((detail, detailIndex) => (
                              <li key={detailIndex} className="flex items-start gap-3">
                                <CheckCircle2 className="h-5 w-5 text-[#C9A64A] flex-shrink-0 mt-0.5" />
                                <span className="text-sm sm:text-base text-gray-300 leading-relaxed">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Elegant Dots Indicator */}
            <div className="flex justify-center gap-3 sm:gap-4 mt-8 sm:mt-16">
              {questions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`relative transition-all duration-500 ${
                    currentSlide === index ? 'w-10 sm:w-16' : 'w-3 sm:w-4'
                  } h-3 sm:h-4 rounded-full overflow-hidden group`}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <div className={`absolute inset-0 rounded-full ${
                    currentSlide === index 
                      ? 'bg-gradient-to-r from-[#C9A64A] to-[#D4B76A]' 
                      : 'bg-[#5A6270]/30 group-hover:bg-[#5A6270]/50'
                  } transition-all duration-300`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-20 sm:py-40 px-4 sm:px-6 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-12 sm:mb-20">
            <div className="inline-block mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
                <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">Partnership</span>
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold text-[#1A1A1C]">
              Who We Work With
            </h2>
          </div>
          
          <div className="space-y-6 sm:space-y-12">
            <div className="glass-card p-6 sm:p-10 rounded-xl sm:rounded-2xl hover:shadow-lg transition-all duration-500 transform hover:-translate-y-2">
              <p className="text-lg sm:text-2xl md:text-3xl text-[#1A1A1C] font-light leading-relaxed">
                We are Virtual CFO partners for those who value <span className="font-semibold text-[#C9A64A]">thinking and clarity</span> over hustle and noise.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 sm:gap-8">
              <div className="glass-card p-6 sm:p-10 rounded-xl sm:rounded-2xl hover:shadow-lg transition-all duration-500 transform hover:-translate-y-2">
                <p className="text-base sm:text-xl md:text-2xl text-[#1A1A1C] font-light leading-relaxed">
                  We are <span className="font-semibold">not for everyone</span>.<br />
                  We charge for value and only work when we can genuinely improve outcomes.
                </p>
              </div>
              
              <div className="glass-card p-6 sm:p-10 rounded-xl sm:rounded-2xl hover:shadow-lg transition-all duration-500 transform hover:-translate-y-2 bg-gradient-to-br from-[#C9A64A]/5 to-transparent">
                <p className="text-base sm:text-xl md:text-2xl text-[#1A1A1C] font-light leading-relaxed">
                  If you are bootstrapped and planning to raise, <span className="font-semibold text-[#C9A64A]">we help first</span>.<br />
                  Raise well, build strong, then retain us.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof Section - New */}
      <section className="py-20 sm:py-40 px-4 sm:px-6 bg-gradient-to-br from-[#F8F8F8] to-white relative overflow-hidden">
        {/* Background Image - Faded */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-[0.04]"
            style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxmaW5hbmNpYWwlMjBhbmFseXNpc3xlbnwwfHx8fDE3NjQxODc3NDN8MA&ixlib=rb-4.1.0&q=85)' }}
          />
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-12 sm:mb-20">
            <div className="inline-block mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
                <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">No Faith Required</span>
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold text-[#1A1A1C] mb-8 sm:mb-12 px-2">
              We do not ask for faith.<br />
              We offer proof.
            </h2>
          </div>

          <div className="glass-card p-8 sm:p-12 md:p-16 rounded-2xl sm:rounded-3xl text-center space-y-6 sm:space-y-8">
            <p className="text-lg sm:text-2xl md:text-3xl text-[#1A1A1C] font-light leading-relaxed">
              Our sample work and calculators are open for you to try at your own pace.
            </p>
            <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] mx-auto rounded-full" />
            <p className="text-base sm:text-xl md:text-2xl text-[#5A6270] font-light leading-relaxed">
              If even one brings ease to your chaos, that is our relationship starting right.
            </p>
            <p className="text-lg sm:text-2xl md:text-3xl text-[#1A1A1C] font-semibold leading-relaxed pt-4 sm:pt-6">
              When value is real, fees become a formality.
            </p>
            
            <div className="pt-6 sm:pt-8">
              <Link to="/calculators">
                <Button 
                  size="lg"
                  className="w-full sm:w-auto bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 px-6 sm:px-10 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl group"
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  Try Free Calculators
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 sm:py-32 px-4 sm:px-6 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C9A64A]/30 to-transparent" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-block mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
                <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">Founder Stories</span>
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold text-[#1A1A1C]">
              What Founders Say
            </h2>
          </div>

          {/* Testimonial Slider */}
          <div className="relative">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-700 ease-out"
                style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="min-w-full px-4">
                    <div className="glass-card p-8 sm:p-12 rounded-2xl sm:rounded-3xl text-center max-w-4xl mx-auto">
                      <Quote className="h-10 w-10 sm:h-12 sm:w-12 text-[#C9A64A]/30 mx-auto mb-6" />
                      <p className="text-lg sm:text-2xl md:text-3xl text-[#1A1A1C] font-light leading-relaxed mb-8 italic">
                        &ldquo;{testimonial.quote}&rdquo;
                      </p>
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-lg sm:text-xl font-bold text-[#1A1A1C]">{testimonial.author}</p>
                        <p className="text-sm sm:text-base text-[#5A6270]">{testimonial.role}, {testimonial.company}</p>
                        <div className="mt-3 px-4 py-2 bg-[#C9A64A]/10 rounded-full">
                          <span className="text-sm font-semibold text-[#C9A64A]">{testimonial.result}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonial Dots */}
            <div className="flex justify-center gap-3 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`transition-all duration-300 rounded-full ${
                    currentTestimonial === index 
                      ? 'w-8 h-3 bg-[#C9A64A]' 
                      : 'w-3 h-3 bg-[#5A6270]/30 hover:bg-[#5A6270]/50'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-20 sm:py-32 px-4 sm:px-6 bg-gradient-to-b from-[#F8F8F8] to-white relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-block mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
                <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">The Process</span>
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold text-[#1A1A1C] mb-4">
              How We Work Together
            </h2>
            <p className="text-base sm:text-xl text-[#5A6270] max-w-2xl mx-auto">
              A structured, transparent process designed to deliver value from day one.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-full w-full h-px bg-gradient-to-r from-[#C9A64A] to-transparent z-0" />
                )}
                <div className="glass-card p-6 sm:p-8 rounded-2xl hover:shadow-xl transition-all duration-300 relative z-10 h-full">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#C9A64A] to-[#D4B76A] flex items-center justify-center">
                      <span className="text-lg sm:text-xl font-bold text-[#0F1A2A]">{step.step}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#5A6270]">
                      <Clock className="h-4 w-4" />
                      {step.duration}
                    </div>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1A1A1C] mb-3">{step.title}</h3>
                  <p className="text-base text-[#5A6270] leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 sm:mt-12">
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 font-bold px-8 py-6 text-lg rounded-xl group">
                Schedule Discovery Call
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Ideal Clients Section */}
      <section className="py-20 sm:py-32 px-4 sm:px-6 bg-white relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-block mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
                <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">Best Fit</span>
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold text-[#1A1A1C] mb-4">
              Who We Work Best With
            </h2>
            <p className="text-base sm:text-xl text-[#5A6270] max-w-2xl mx-auto">
              We deliver the most value for founders at these stages.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {idealClients.map((client, index) => (
              <div key={index} className="glass-card p-6 sm:p-8 rounded-2xl hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#C9A64A]/30 group">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#C9A64A]/10 flex items-center justify-center mb-5 group-hover:bg-[#C9A64A]/20 transition-colors">
                  <client.icon className="h-7 w-7 sm:h-8 sm:w-8 text-[#C9A64A]" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#1A1A1C] mb-2">{client.stage}</h3>
                <p className="text-base text-[#5A6270] mb-4 leading-relaxed">{client.description}</p>
                <div className="inline-block px-3 py-1 bg-[#0F1A2A] rounded-full">
                  <span className="text-sm font-semibold text-[#C9A64A]">{client.revenue}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team Section */}
      <section className="py-20 sm:py-32 px-4 sm:px-6 bg-gradient-to-b from-[#F8F8F8] to-white relative">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-block mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
                <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">The Team</span>
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold text-[#1A1A1C]">
              Who You Work With
            </h2>
          </div>

          <div className="glass-card p-8 sm:p-12 rounded-2xl sm:rounded-3xl">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-gradient-to-br from-[#C9A64A] to-[#D4B76A] flex items-center justify-center flex-shrink-0">
                <Users className="h-16 w-16 sm:h-20 sm:w-20 text-[#0F1A2A]" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#1A1A1C] mb-2">Virtual CFO Team</h3>
                <p className="text-[#C9A64A] font-semibold mb-4">Experienced Finance Professionals</p>
                <p className="text-base sm:text-lg text-[#5A6270] leading-relaxed mb-6">
                  Our team brings 15+ years of combined experience across startup finance, investment banking, and FP&A. 
                  We have helped 50+ startups raise over $100M in funding and build financial systems that scale.
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#F8F8F8] rounded-full">
                    <Award className="h-4 w-4 text-[#C9A64A]" />
                    <span className="text-sm font-medium text-[#1A1A1C]">CPA & CFA Certified</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#F8F8F8] rounded-full">
                    <Briefcase className="h-4 w-4 text-[#C9A64A]" />
                    <span className="text-sm font-medium text-[#1A1A1C]">Ex-Big 4 & VC-backed</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#F8F8F8] rounded-full">
                    <Star className="h-4 w-4 text-[#C9A64A]" />
                    <span className="text-sm font-medium text-[#1A1A1C]">50+ Startups Served</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Are Changing */}
      <section className="py-20 sm:py-40 px-4 sm:px-6 hero-gradient text-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-20 left-4 sm:left-10 w-1 sm:w-2 h-48 sm:h-96 bg-gradient-to-b from-[#C9A64A] to-transparent opacity-20 rounded-full" />
        <div className="absolute bottom-20 right-4 sm:right-10 w-1 sm:w-2 h-48 sm:h-96 bg-gradient-to-t from-[#C9A64A] to-transparent opacity-20 rounded-full" />
        
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-12 sm:mb-24">
            <div className="inline-block mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
                <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">Our Impact</span>
                <div className="h-px w-6 sm:w-8 bg-[#C9A64A]" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold">
              What We Are Changing
            </h2>
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            {changes.map((change, index) => (
              <div
                key={index}
                ref={(el) => (observerRefs.current[index] = el)}
                className={`premium-list-item flex items-start gap-4 sm:gap-6 p-4 sm:p-8 rounded-xl sm:rounded-2xl transition-all duration-700 transform hover:scale-105 ${
                  visibleItems.has(index)
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 -translate-x-12'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#C9A64A]/20 backdrop-blur-sm flex items-center justify-center border border-[#C9A64A]/40">
                  <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-[#C9A64A]" />
                </div>
                <p className="text-base sm:text-xl md:text-2xl font-light leading-relaxed flex-1 pt-1 sm:pt-2">
                  {change}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 sm:py-40 px-4 sm:px-6 bg-gradient-to-b from-[#1A1A1C] to-[#0F1A2A] text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-48 sm:w-96 h-48 sm:h-96 bg-[#C9A64A] opacity-5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-48 sm:w-96 h-48 sm:h-96 bg-[#C9A64A] opacity-5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-5xl mx-auto text-center space-y-10 sm:space-y-16 relative z-10">
          <div className="space-y-6 sm:space-y-10">
            <div className="inline-block">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
                <div className="h-px w-8 sm:w-12 bg-[#C9A64A]" />
                <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">Ready to Begin?</span>
                <div className="h-px w-8 sm:w-12 bg-[#C9A64A]" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-5xl md:text-7xl font-bold leading-tight px-2">
              Virtual CFO is not a service.<br />
              <span className="text-gradient">It is stability.</span>
            </h2>
            <p className="text-base sm:text-xl md:text-2xl text-gray-300 font-light leading-relaxed max-w-3xl mx-auto px-4">
              Let us free your finance from clutter and build a system you trust.
            </p>
          </div>

          <div className="flex flex-col gap-4 sm:gap-8 justify-center items-center pt-4 sm:pt-8 px-4">
            <Link to="/calculators">
              <Button 
                size="lg"
                className="premium-button w-full sm:w-auto bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:shadow-2xl hover:scale-110 transition-all duration-500 px-8 sm:px-12 py-6 sm:py-8 text-lg sm:text-xl font-bold rounded-xl sm:rounded-2xl group"
              >
                Begin your model
                <ArrowRight className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-2 transition-transform duration-300" />
              </Button>
            </Link>
            <a href="mailto:twopearlsprivatelimited@gmail.com?subject=Strategy%20Call%20Request&body=Hi%2C%0A%0AI%20would%20like%20to%20schedule%20a%20strategy%20call%20to%20discuss%20Virtual%20CFO%20services.%0A%0ACompany%20Name%3A%20%0AYour%20Name%3A%20%0APreferred%20Time%3A%20%0A%0AThank%20you!">
              <Button 
                size="lg"
                className="glass-button w-full sm:w-auto border-2 border-[#C9A64A]/50 text-white hover:border-[#C9A64A] hover:bg-[#C9A64A]/10 backdrop-blur-sm transition-all duration-500 px-8 sm:px-12 py-6 sm:py-8 text-lg sm:text-xl font-semibold rounded-xl sm:rounded-2xl"
              >
                Schedule a strategy call
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Premium Footer */}
      <footer className="py-12 sm:py-20 px-4 sm:px-6 bg-[#0F0F10] border-t border-[#C9A64A]/20 relative overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#C9A64A]/5 to-transparent" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-10 sm:mb-16">
            <div className="space-y-4 sm:space-y-6 sm:col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="relative">
                  <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-[#C9A64A]" />
                  <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-[#C9A64A] absolute -bottom-1 -right-1" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white">Virtual<span className="text-[#C9A64A]">CFO</span></h3>
              </div>
              <p className="text-gray-400 font-light leading-relaxed text-sm sm:text-base">
                Clarity and control for fast-moving founders who refuse to settle for chaos.
              </p>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              <h4 className="text-white font-semibold text-base sm:text-lg mb-4 sm:mb-6">Quick Links</h4>
              <div className="flex flex-col gap-2 sm:gap-3">
                <Link to="/approach" className="text-gray-400 hover:text-[#C9A64A] transition-colors duration-300 font-light text-sm sm:text-base">Our Approach</Link>
                <Link to="/free-resources" className="text-gray-400 hover:text-[#C9A64A] transition-colors duration-300 font-light text-sm sm:text-base">Free Resources</Link>
                <Link to="/calculators" className="text-gray-400 hover:text-[#C9A64A] transition-colors duration-300 font-light text-sm sm:text-base">Calculators</Link>
                <Link to="/articles" className="text-gray-400 hover:text-[#C9A64A] transition-colors duration-300 font-light text-sm sm:text-base">Articles & Guides</Link>
                <Link to="/contact" className="text-gray-400 hover:text-[#C9A64A] transition-colors duration-300 font-light text-sm sm:text-base">Contact Us</Link>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <h4 className="text-white font-semibold text-base sm:text-lg mb-4 sm:mb-6">Get Started</h4>
              <p className="text-gray-400 font-light leading-relaxed mb-4 text-sm sm:text-base">
                Ready to transform your financial operations?
              </p>
              <a href="mailto:twopearlsprivatelimited@gmail.com?subject=Virtual%20CFO%20Inquiry&body=Hi%2C%0A%0AI%20am%20interested%20in%20learning%20more%20about%20Virtual%20CFO%20services.%0A%0ACompany%20Name%3A%20%0AYour%20Name%3A%20%0A%0AThank%20you!">
                <Button 
                  size="sm"
                  className="bg-[#C9A64A] text-[#0F1A2A] hover:bg-white hover:scale-105 transition-all duration-300 font-semibold text-sm"
                >
                  Book a Call
                </Button>
              </a>
            </div>
          </div>
          
          <div className="pt-8 sm:pt-12 border-t border-[#5A6270]/30 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-6">
            <p className="text-gray-500 text-xs sm:text-sm font-light text-center sm:text-left">
              © 2025 Virtual CFO. All rights reserved.
            </p>
            <div className="flex gap-6 sm:gap-8">
              <Link to="/privacy-policy" className="text-gray-500 hover:text-[#C9A64A] transition-colors duration-300 text-xs sm:text-sm font-light">Privacy Policy</Link>
              <Link to="/disclaimer" className="text-gray-500 hover:text-[#C9A64A] transition-colors duration-300 text-xs sm:text-sm font-light">Disclaimer</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
